import RightSlidingPanel from "components/RightSlidingPanel/RightSlidingPanel";
import moment from "moment-timezone";
import React, { useEffect, useMemo, useRef } from "react";
import { useDrag } from "react-dnd";
import styled from "styled-components";

import crossIcon from "../../../assets/img/icons/cross_black.svg";
import noAssigneeIcon from "../../../assets/img/icons/no-assignee.svg";
import { CommonFlex, CommonText } from "../../../styledComponents/common";
import { parseDateTimeInTimezone } from "../shiftAndScheduleHelper";

// Match existing DnD type used in ShiftSchedule.jsx
const DRAG_TYPES = {
  CALENDAR_EVENT: "CALENDAR_EVENT",
};

const PanelInner = styled.div`
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
`;

const Header = styled.div`
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 2;
  background: ${({ theme }) => theme.colors.main.white || "#FFFFFF"};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding: 16px 16px 12px 16px;
  border-bottom: 1px solid #efefef;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CloseButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid #efefef;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding: 12px 16px 16px 16px;
`;

const ShiftCard = styled.div`
  width: 100%;
  border-radius: 8px;
  border: 1px solid #efefef;
  background: ${({ bg, theme }) => bg || theme.colors.main.white || "#FFFFFF"};
  padding: 10px 10px;
  box-sizing: border-box;
  /* cursor and opacity are now handled inline via style prop */
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ color }) => color || "#2266EF"};
  flex-shrink: 0;
`;

const getAccent = shift => {
  const isJob = Boolean(shift?.work_order?.id);
  const isOffice = Boolean(shift?.office);
  return isOffice ? "#6C2EF2" : isJob ? "#2266EF" : "#2266EF";
};

const getBackground = shift => {
  const isJob = Boolean(shift?.work_order?.id);
  const isOffice = Boolean(shift?.office);
  if (isOffice) return "#f4ecfe"; // Shift background color (matches legend)
  if (isJob) return "#edf8fe"; // Job background color (matches legend)
  return "#D2E4FE";
};

const getRightLabel = shift => {
  if (shift?.office?.name) return shift.office.name;
  const a0 = Array.isArray(shift?.assignees) ? shift.assignees[0] : null;
  return (
    a0?.name || a0?.full_name || shift?.team?.name || shift?.crew?.name || ""
  );
};

const DraggableShiftRow = ({
  shift,
  timeZone,
  onSelectShift,
  selectedOrganization,
}) => {
  const didDragRef = useRef(false);

  // Check if event has already started
  const isEventPast = useMemo(() => {
    if (!shift?.start_time) return false;
    const orgTimezone =
      selectedOrganization?.timezone || timeZone || moment.tz.guess();
    const eventStart = parseDateTimeInTimezone(shift.start_time, orgTimezone);
    if (!eventStart) return false;
    const now = moment.tz(orgTimezone);
    return eventStart.isBefore(now);
  }, [shift?.start_time, selectedOrganization?.timezone, timeZone]);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: DRAG_TYPES.CALENDAR_EVENT,
      item: { eventId: shift?.id },
      canDrag: () => {
        return Boolean(shift?.id);
      },
      collect: monitor => {
        const dragging = monitor.isDragging();
        if (dragging) didDragRef.current = true;
        return { isDragging: dragging };
      },
      end: () => {
        // Reset after the click event (if any) has had a chance to fire.
        setTimeout(() => {
          didDragRef.current = false;
        }, 0);
      },
    }),
    [shift?.id, isEventPast],
  );

  const accent = getAccent(shift);

  const isJob = Boolean(shift?.work_order?.id);
  const isOffice = Boolean(shift?.office);
  const hasNoAssignee = isJob
    ? !(
        shift?.assignee_id ||
        shift?.assignee?.id ||
        shift?.assignee ||
        shift?.assigned_to_id ||
        shift?.assigned_to?.id ||
        shift?.assigned_to ||
        shift?.user_id ||
        shift?.user?.id ||
        (Array.isArray(shift?.assignees) && shift.assignees.length > 0)
      )
    : isOffice
    ? !(Array.isArray(shift?.assignees) && shift.assignees.length > 0)
    : false;

  const startText = shift?.start_time
    ? parseDateTimeInTimezone(shift.start_time, timeZone)?.format("h:mm a")
    : "";
  const endText = shift?.end_time
    ? parseDateTimeInTimezone(shift.end_time, timeZone)?.format("h:mm a")
    : "";
  const timeRangeText =
    startText && endText ? `${startText} – ${endText}` : startText;
  const rightLabel = getRightLabel(shift);
  const bg = getBackground(shift);

  return (
    <ShiftCard
      ref={drag}
      isDragging={isDragging}
      bg={bg}
      style={{
        cursor: isDragging ? "grabbing" : "grab",
        opacity: isDragging ? 0.5 : 1,
      }}
      onMouseDown={e => {
        // Track "real" clicks vs drag gestures.
        e.stopPropagation();
        didDragRef.current = false;
      }}
      onTouchStart={e => {
        e.stopPropagation();
      }}
      onClick={e => {
        // Prevent click-to-open after completing a drag gesture.
        if (didDragRef.current) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        if (typeof onSelectShift === "function") {
          onSelectShift(shift);
        }
      }}
      role={typeof onSelectShift === "function" ? "button" : undefined}
      tabIndex={typeof onSelectShift === "function" ? 0 : undefined}
      onKeyDown={e => {
        if (typeof onSelectShift !== "function") return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelectShift(shift);
        }
      }}
    >
      <CommonFlex
        direction="column"
        gap="6px"
        alignItems="flex-start"
        style={{ width: "100%" }}
      >
        <CommonFlex
          alignItems="center"
          justifyContent="space-between"
          gap="8px"
          style={{ width: "100%" }}
        >
          <CommonFlex alignItems="center" gap="6px" style={{ minWidth: 0 }}>
            <Dot color={accent} />
            <CommonText
              fontSize="12px"
              fontWeight="700"
              margin="0"
              style={{ whiteSpace: "nowrap", color: "#1F2937" }}
            >
              {timeRangeText}
            </CommonText>
            {hasNoAssignee ? (
              <img
                src={noAssigneeIcon}
                alt="No assignee"
                title="No assignee"
                style={{ width: 12, height: 12, flexShrink: 0 }}
              />
            ) : null}
          </CommonFlex>
          {rightLabel ? (
            <CommonText
              fontSize="11px"
              fontWeight="500"
              margin="0"
              style={{ color: "#6B7280", whiteSpace: "nowrap" }}
            >
              {rightLabel}
            </CommonText>
          ) : null}
        </CommonFlex>
        <CommonText
          fontSize="12px"
          fontWeight="500"
          margin="0"
          style={{
            width: "100%",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            color: "#353A46",
          }}
          title={shift?.name || shift?.title || ""}
        >
          {shift?.name || shift?.title || ""}
        </CommonText>
      </CommonFlex>
    </ShiftCard>
  );
};

/**
 * Right side “Show more” panel.
 * - Uses the same RightSlidingPanel pattern as ApprovalRightpanel.jsx
 * - Overlay is pushed behind everything so it never blocks the calendar
 * - Width is derived from (passed in) unscheduled container width
 * - Items are draggable (CALENDAR_EVENT) -> can be dropped on calendar targets
 */
const ShowMoreRightSidePanel = ({
  isOpen,
  toggle,
  shifts,
  selectedOrganization,
  selectedDate,
  width,
  onEnsureUnscheduledExpanded,
  onSelectShift,
  onCloseUnscheduled,
}) => {
  const timeZone = selectedOrganization?.timezone || moment.tz.guess();
  const panelRef = useRef(null);

  // Ensure the unscheduled container is expanded when panel opens (so width matches).
  useEffect(() => {
    if (isOpen && typeof onEnsureUnscheduledExpanded === "function") {
      onEnsureUnscheduledExpanded();
    }
  }, [isOpen, onEnsureUnscheduledExpanded]);

  // Close on outside click (overlay is behind the page to avoid blocking calendar interactions).
  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = e => {
      // While dragging, ignore outside clicks/mouse downs.
      if (
        typeof document !== "undefined" &&
        document.body?.classList?.contains("rbc-dnd-dragging")
      ) {
        return;
      }

      const el = panelRef.current;
      if (!el) return;

      if (!el.contains(e.target)) {
        if (typeof toggle === "function") toggle();
        if (typeof onCloseUnscheduled === "function") onCloseUnscheduled();
      }
    };

    document.addEventListener("mousedown", onPointerDown, true);
    document.addEventListener("touchstart", onPointerDown, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown, true);
      document.removeEventListener("touchstart", onPointerDown, true);
    };
  }, [isOpen, toggle, onCloseUnscheduled]);

  return (
    <RightSlidingPanel
      isOpen={isOpen}
      closePanel={toggle}
      width={width}
      // Critical: do not block calendar interactions with an overlay
      overlayZIndex={-1}
      contentZIndex={5}
    >
      <PanelInner ref={panelRef}>
        <Header>
          <HeaderText>
            <CommonText
              margin="0"
              fontSize="12px"
              fontWeight="600"
              style={{ color: "#6B7280" }}
            >
              Job list for
            </CommonText>
            <CommonText
              margin="0"
              fontSize="14px"
              fontWeight="700"
              style={{ color: "#111827" }}
            >
              {selectedDate
                ? moment(selectedDate).format("D MMM YYYY, dddd")
                : "Shifts"}
            </CommonText>
          </HeaderText>

          <CloseButton type="button" onClick={toggle} aria-label="Close panel">
            <img src={crossIcon} alt="" width="14" height="14" />
          </CloseButton>
        </Header>

        <List>
          {Array.isArray(shifts) && shifts.length > 0 ? (
            shifts.map(shift => (
              <DraggableShiftRow
                key={shift?.id}
                shift={shift}
                timeZone={timeZone}
                onSelectShift={onSelectShift}
                selectedOrganization={selectedOrganization}
              />
            ))
          ) : (
            <CommonText margin="0" fontSize="12px">
              No items
            </CommonText>
          )}
        </List>
      </PanelInner>
    </RightSlidingPanel>
  );
};

export default ShowMoreRightSidePanel;
